import { Clock } from 'lucide-react';
import { AnchoredSelect, type AnchoredSelectOption } from '../AnchoredSelect';
import { isQuarterHourTimeValue, QUARTER_HOUR_TIME_OPTIONS } from '../../../utils';

interface QuarterHourTimeSelectProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: string;
    className?: string;
    id?: string;
    placeholder?: string;
    hideLabel?: boolean;
    /** Excluye esta hora y todas las anteriores. */
    minExclusiveValue?: string;
    /** Excluye esta hora y todas las posteriores. */
    maxExclusiveValue?: string;
}

const TIME_OPTIONS: AnchoredSelectOption[] = QUARTER_HOUR_TIME_OPTIONS.map((time) => ({
    value: time,
    label: time,
}));

/** Selector de hora dentro del app, limitado al viewport y en intervalos de 15 minutos. */
export function QuarterHourTimeSelect({
    label,
    value,
    onChange,
    disabled = false,
    error,
    className,
    id,
    placeholder = 'Selecciona',
    hideLabel = false,
    minExclusiveValue,
    maxExclusiveValue,
}: QuarterHourTimeSelectProps) {
    const selectedValue = value.trim().slice(0, 5);
    const hasUnsupportedTime = selectedValue !== '' && !isQuarterHourTimeValue(selectedValue);
    const minimum = minExclusiveValue?.trim().slice(0, 5);
    const maximum = maxExclusiveValue?.trim().slice(0, 5);
    const optionsWithinRange = TIME_OPTIONS.filter(({ value: optionValue }) => (
        (!minimum || !isQuarterHourTimeValue(minimum) || optionValue > minimum)
        && (!maximum || !isQuarterHourTimeValue(maximum) || optionValue < maximum)
    ));
    const hasOutOfRangeSelection = selectedValue !== ''
        && !optionsWithinRange.some((option) => option.value === selectedValue);
    const options = hasUnsupportedTime || hasOutOfRangeSelection
        ? [{ value: selectedValue, label: selectedValue }, ...optionsWithinRange]
        : optionsWithinRange;

    return (
        <AnchoredSelect
            id={id}
            label={label}
            value={selectedValue}
            options={options}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            error={error ?? (hasUnsupportedTime ? 'Selecciona una hora en intervalos de 15 minutos.' : undefined)}
            className={className}
            hideLabel={hideLabel}
            icon={<Clock size={20} />}
        />
    );
}
